import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowLeft, Loader } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_BASE } from '../lib/api';

const API = API_BASE;

const BlogDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const lang = i18n.language;

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(res => setPost(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      <Navbar />
      <div data-testid="blog-detail-page" className="min-h-screen pt-16 bg-[#F5F5F5] dark:bg-[#0F1C2E]">
        {loading ? (
          <div className="flex justify-center py-32"><Loader size={24} className="animate-spin text-[#7D468C]" /></div>
        ) : error || !post ? (
          <div className="text-center py-32">
            <p className="text-[#233559]/50 dark:text-white/40 font-source-sans mb-4">Artículo no encontrado.</p>
            <Link to="/blog" className="text-[#7D468C] font-poppins font-bold text-sm uppercase tracking-wide hover:text-[#9D62AD]">
              ← {t('blog.back')}
            </Link>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="py-16 bg-[#233559] dark:bg-[#0F1C2E] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-56 h-56 bg-[#7D468C]/15 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'top right' }} />
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-poppins mb-6 transition-colors"
                >
                  <ArrowLeft size={14} /> {t('blog.back')}
                </Link>
                {post.category && (
                  <div className="inline-block bg-[#7D468C] text-white text-xs px-3 py-1 font-poppins font-bold uppercase tracking-wide mb-4">
                    {post.category}
                  </div>
                )}
                <h1 className="font-poppins font-black text-4xl sm:text-5xl text-white mb-4 leading-tight">
                  {lang === 'en' ? post.title_en : post.title_es}
                </h1>
                <div className="flex items-center gap-4 text-white/50 text-sm font-source-sans">
                  <span className="flex items-center gap-1.5"><User size={13} /> {post.author}</span>
                  <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(post.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Featured image */}
            {post.featured_image && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
                <img src={post.featured_image} alt={lang === 'en' ? post.title_en : post.title_es} className="w-full aspect-video object-cover" />
              </div>
            )}

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="bg-white dark:bg-[#1A2639] p-8 sm:p-12">
                {(lang === 'en' ? post.excerpt_en : post.excerpt_es) && (
                  <p className="text-lg text-[#233559]/70 dark:text-white/60 font-source-sans mb-8 pb-8 border-b border-[#233559]/10 dark:border-white/10 leading-relaxed italic">
                    {lang === 'en' ? post.excerpt_en : post.excerpt_es}
                  </p>
                )}
                <div
                  className="font-source-sans text-[#233559]/80 dark:text-white/75 leading-relaxed whitespace-pre-line text-base"
                  style={{ lineHeight: '1.8' }}
                >
                  {lang === 'en' ? post.body_en : post.body_es}
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-[#233559]/10 dark:border-white/10">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-[#7D468C] hover:text-[#9D62AD] font-poppins font-bold text-sm uppercase tracking-wide transition-colors"
                >
                  <ArrowLeft size={14} /> {t('blog.back')}
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
};

export default BlogDetail;
